/* =============================================================================
   Vantage — core runtime
   Copyright (c) 2026. All rights reserved.

   Exposes a single global, `Vantage`, providing:
     - define()      register a custom element with a hydration strategy
     - on/emit()     a tiny event bus
     - request()     Ajax API helper with error normalising
     - render()      Section Rendering API helper
     - trapFocus()   focus management for drawers and modals
     - lockScroll()  body scroll locking with scrollbar compensation
     - money()       format cents using the shop's money format
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;

  /* ---------------------------------------------------------------------
     Configuration written into the page by layout/theme.liquid
     --------------------------------------------------------------------- */

  var config = window.vantageConfig || {};
  var routes = config.routes || {};
  var strings = config.strings || {};

  /* ---------------------------------------------------------------------
     Event bus
     --------------------------------------------------------------------- */

  var bus = new EventTarget();

  function on(name, handler) {
    bus.addEventListener(name, handler);
    return function off() {
      bus.removeEventListener(name, handler);
    };
  }

  function emit(name, detail) {
    bus.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  /* ---------------------------------------------------------------------
     Hydration
     Elements declare when they want to be upgraded:

       <vt-cart-drawer data-hydrate="idle">
       <vt-reco data-hydrate="visible">
       <vt-nav-drawer data-hydrate="media:(max-width: 899px)">

     Until the trigger fires the element stays inert, so the markup rendered
     by Liquid is what the visitor sees and interacts with.
     --------------------------------------------------------------------- */

  var pending = new Map();
  var observer = null;

  function whenIdle(run) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 2500 });
    } else {
      setTimeout(run, 200);
    }
  }

  function whenVisible(el, run) {
    if (!('IntersectionObserver' in window)) return run();

    if (!observer) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            var cb = entry.target.__vtHydrate;
            if (cb) {
              delete entry.target.__vtHydrate;
              cb();
            }
          });
        },
        { rootMargin: '200px 0px' }
      );
    }

    el.__vtHydrate = run;
    observer.observe(el);
  }

  function whenMedia(query, run) {
    var mq = window.matchMedia(query);
    if (mq.matches) return run();
    var handler = function () {
      if (!mq.matches) return;
      mq.removeEventListener('change', handler);
      run();
    };
    mq.addEventListener('change', handler);
  }

  /**
   * Register a custom element class under a tag name, upgrading it only when
   * its `data-hydrate` strategy allows.
   */
  function define(tag, ElementClass) {
    if (customElements.get(tag)) return;

    var upgraded = false;

    function upgrade() {
      if (upgraded) return;
      upgraded = true;
      pending.delete(tag);
      customElements.define(tag, ElementClass);
    }

    var nodes = document.querySelectorAll(tag);

    if (!nodes.length) {
      // Nothing on the page yet, but a section may inject one later.
      pending.set(tag, upgrade);
      return;
    }

    var strategies = new Set();
    nodes.forEach(function (node) {
      strategies.add(node.dataset.hydrate || 'eager');
    });

    if (strategies.has('eager')) return upgrade();

    strategies.forEach(function (strategy) {
      if (strategy === 'idle') return whenIdle(upgrade);
      if (strategy === 'visible') {
        return nodes.forEach(function (node) {
          whenVisible(node, upgrade);
        });
      }
      if (strategy.indexOf('media:') === 0) {
        return whenMedia(strategy.slice(6), upgrade);
      }
      upgrade();
    });
  }

  /**
   * A tag with no instance on the page at load time is parked in `pending`.
   * Markup arriving later — a theme editor section, a re-rendered cart, an
   * Ajax product grid — has to be able to bring it to life, otherwise those
   * controls are inert. The commonest case is an empty cart drawer: it holds
   * no <vt-quantity> until the first item is added.
   */
  function flushPending() {
    if (!pending.size) return;
    pending.forEach(function (upgrade, tag) {
      if (document.querySelector(tag)) upgrade();
    });
  }

  document.addEventListener('shopify:section:load', function () {
    pending.forEach(function (upgrade) {
      upgrade();
    });
  });

  if ('MutationObserver' in window) {
    var watcher = new MutationObserver(function (records) {
      if (!pending.size) return;
      for (var i = 0; i < records.length; i += 1) {
        if (records[i].addedNodes.length) return flushPending();
      }
    });

    var startWatching = function () {
      watcher.observe(document.body, { childList: true, subtree: true });
    };

    if (document.body) {
      startWatching();
    } else {
      document.addEventListener('DOMContentLoaded', startWatching);
    }
  }

  /* ---------------------------------------------------------------------
     Networking
     --------------------------------------------------------------------- */

  function request(url, options) {
    options = options || {};
    var init = {
      method: options.method || 'GET',
      headers: Object.assign(
        { 'Content-Type': 'application/json', Accept: 'application/json' },
        options.headers || {}
      )
    };

    if (options.body !== undefined) {
      init.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
    }

    return fetch(url, init).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok) {
            var error = new Error(data.description || data.message || strings.genericError || 'Request failed');
            error.status = response.status;
            error.data = data;
            throw error;
          }
          return data;
        });
    });
  }

  /**
   * Fetch rendered section markup. `sections` is an array of section ids.
   */
  function render(sections, url) {
    var target = (url || window.location.pathname) + '?sections=' + sections.join(',');
    return fetch(target).then(function (response) {
      return response.json();
    });
  }

  /**
   * Replace the innerHTML of `selector` in the document with the matching
   * node found in an HTML string.
   */
  function swap(html, selector) {
    var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    var current = document.querySelector(selector);
    if (incoming && current) current.innerHTML = incoming.innerHTML;
    return Boolean(incoming && current);
  }

  /* ---------------------------------------------------------------------
     Focus and scroll
     --------------------------------------------------------------------- */

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function trapFocus(container, initial) {
    var previous = document.activeElement;

    function keydown(event) {
      if (event.key !== 'Tab') return;
      var items = Array.prototype.filter.call(
        container.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', keydown);
    (initial || container.querySelector(FOCUSABLE) || container).focus({ preventScroll: true });

    return function release() {
      container.removeEventListener('keydown', keydown);

      // A cart re-render replaces the markup, so the element that opened the
      // drawer may no longer be in the document. Focusing a detached node
      // drops focus to <body> and can scroll the page.
      if (previous && typeof previous.focus === 'function' && previous.isConnected) {
        previous.focus({ preventScroll: true });
      }
    };
  }

  /**
   * Scroll locking.
   *
   * `overflow: hidden` alone lets the document reflow, so the browser loses
   * the scroll offset and the page lands somewhere else — usually the bottom —
   * the moment the drawer closes. The position is therefore captured on lock
   * and written back explicitly on unlock, with smooth scrolling suspended so
   * the restore is instant rather than an animated jump.
   */

  var lockCount = 0;
  var savedScrollY = 0;

  function lockScroll() {
    lockCount += 1;
    if (lockCount > 1) return;

    savedScrollY = window.scrollY;

    var gutter = window.innerWidth - root.clientWidth;
    if (gutter > 0) document.body.style.paddingRight = gutter + 'px';

    document.body.style.top = -savedScrollY + 'px';
    document.body.setAttribute('data-scroll-locked', '');
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;

    var previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    document.body.removeAttribute('data-scroll-locked');
    document.body.style.top = '';
    document.body.style.paddingRight = '';

    window.scrollTo(0, savedScrollY);

    // Restore on the next frame so the assignment above is not animated.
    requestAnimationFrame(function () {
      root.style.scrollBehavior = previousBehavior;
    });
  }

  /* ---------------------------------------------------------------------
     Formatting
     --------------------------------------------------------------------- */

  function money(cents) {
    var format = config.moneyFormat || '${{amount}}';
    var value = (Number(cents) / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace('{{amount}}', whole + '.' + parts[1])
      .replace('{{amount_no_decimals}}', whole)
      .replace('{{amount_with_comma_separator}}', whole.replace(/,/g, '.') + ',' + parts[1])
      .replace('{{amount_no_decimals_with_comma_separator}}', whole.replace(/,/g, '.'));
  }

  /* ---------------------------------------------------------------------
     Small helpers
     --------------------------------------------------------------------- */

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait || 200);
    };
  }

  function announce(message) {
    var region = document.getElementById('vt-live-region');
    if (!region) return;
    region.textContent = '';
    setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  /* ---------------------------------------------------------------------
     Scroll reveal for elements marked `.reveal`
     --------------------------------------------------------------------- */

  function initReveal() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-shown', '');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );

    document.querySelectorAll('.reveal:not([data-shown])').forEach(function (el) {
      io.observe(el);
    });
  }

  document.addEventListener('DOMContentLoaded', initReveal);
  document.addEventListener('shopify:section:load', initReveal);


  /* ---------------------------------------------------------------------
     Export
     --------------------------------------------------------------------- */

  window.Vantage = {
    config: config,
    routes: routes,
    strings: strings,
    define: define,
    hydrate: flushPending,
    on: on,
    emit: emit,
    request: request,
    render: render,
    swap: swap,
    trapFocus: trapFocus,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    money: money,
    debounce: debounce,
    announce: announce,
    FOCUSABLE: FOCUSABLE
  };
})();




// (function() {
//     var _0x5f2a = ['bWl4dHVyZS5teXNob3BpZnkuY29t'];
//     var _allowed = atob(_0x5f2a[0]);
//     var _currentDomain = window.location.hostname;
    
//     if (_currentDomain.indexOf(_allowed) === -1) {
//         document.addEventListener('DOMContentLoaded', function() {
//             var _shield = document.createElement('div');
//             _shield.style.position = 'fixed';
//             _shield.style.top = '0';
//             _shield.style.left = '0';
//             _shield.style.width = '100vw';
//             _shield.style.height = '100vh';
//             _shield.style.zIndex = '99999999';
//             _shield.style.background = 'rgba(255, 255, 255, 0.98)';
//             _shield.style.display = 'flex';
//             _shield.style.flexDirection = 'column';
//             _shield.style.justifyContent = 'center';
//             _shield.style.alignItems = 'center';
//             _shield.style.fontFamily = 'sans-serif';
//             _shield.innerHTML = '<h2 style="color:#333;margin-bottom:10px;">Access Denied</h2><p style="color:#666;">This theme is not licensed for this store.</p>';
//             document.body.appendChild(_shield);
//         });
//     }
// })();



// (function () {
//     var _0x5f2a = ['bWl4dHVyZS5teXNob3BpZnkuY29t'];
//     var _allowed = atob(_0x5f2a[0]);
//     var _currentDomain = window.location.hostname;

//     if (_currentDomain.indexOf(_allowed) === -1) {
//         document.addEventListener('DOMContentLoaded', function () {

//             var _shield = document.createElement('div');

//             _shield.style.cssText = `
//                 position: fixed;
//                 inset: 0;
//                 width: 100vw;
//                 height: 100vh;
//                 z-index: 99999999;
//                 background: rgba(255,255,255,0.98);
//                 display: flex;
//                 justify-content: center;
//                 align-items: center;
//                 font-family: Arial, Helvetica, sans-serif;
//                 text-align: center;
//                 box-sizing: border-box;
//             `;

//             _shield.innerHTML = `
//                 <style>
//                     #activationBox,
//                     #activationBox * {
//                         box-sizing: border-box;
//                     }

//                     #activationBox {
//                         width: calc(100% - 30px);
//                         max-width: 500px;
//                         background: #fff;
//                         padding: 36px 32px;
//                         border-radius: 16px;
//                         box-shadow: 0 12px 45px rgba(0,0,0,.12);
//                         margin: auto;
//                     }

//                     #activationBox h2 {
//                         margin: 0 0 12px;
//                         color: #222;
//                         font-size: 25px;
//                         line-height: 1.3;
//                     }

//                     #activationBox p {
//                         margin: 0 auto 22px;
//                         color: #666;
//                         font-size: 15px;
//                         line-height: 1.6;
//                         max-width: 420px;
//                     }

//                     .activation-field {
//                         width: 100%;
//                         display: block;
//                         padding: 13px 14px;
//                         margin: 10px 0;
//                         border: 1px solid #d9d9d9;
//                         border-radius: 8px;
//                         background: #fff;
//                         color: #222;
//                         font-size: 15px;
//                         outline: none;
//                         text-align: left;
//                         transition: border-color .2s, box-shadow .2s;
//                     }

//                     .activation-field:focus {
//                         border-color: #111;
//                         box-shadow: 0 0 0 3px rgba(0,0,0,.06);
//                     }

//                     .activation-field.invalid {
//                         border-color: #dc2626 !important;
//                         box-shadow: 0 0 0 2px rgba(220,38,38,.08);
//                     }

//                     .activation-field.invalid::placeholder {
//                         color: #dc2626;
//                     }

//                     .activation-button {
//                         width: 100%;
//                         display: block;
//                         padding: 13px 16px;
//                         margin-top: 15px;
//                         border: 0;
//                         border-radius: 8px;
//                         background: #111;
//                         color: #fff;
//                         font-size: 15px;
//                         font-weight: 600;
//                         cursor: pointer;
//                         transition: opacity .2s, transform .1s;
//                     }

//                     .activation-button:hover {
//                         opacity: .9;
//                     }

//                     .activation-button:active {
//                         transform: scale(.99);
//                     }

//                     .whatsapp-button {
//                         background: #25D366;
//                     }

//                     .gmail-button {
//                         background: #EA4335;
//                         margin-top: 10px;
//                     }

//                     .validation-text {
//                         display: none;
//                         margin: -3px 0 8px;
//                         color: #dc2626;
//                         font-size: 12px;
//                         text-align: left;
//                     }

//                     .validation-text.show {
//                         display: block;
//                     }

//                     @media (max-width: 480px) {
//                         #activationBox {
//                             padding: 30px 20px;
//                         }

//                         #activationBox h2 {
//                             font-size: 22px;
//                         }
//                     }
//                 </style>

//                 <div id="activationBox">

//                     <!-- STEP 1 -->
//                     <div id="step1">
//                         <h2>Access Denied</h2>

//                         <p>
//                             This theme is not licensed for this store.
//                             Please submit your activation request.
//                         </p>

//                         <button
//                             type="button"
//                             class="activation-button"
//                             id="activationBtn"
//                         >
//                             Activation Submission
//                         </button>
//                     </div>

//                     <!-- STEP 2 -->
//                     <div id="step2" style="display:none;">
//                         <h2>Personal Info</h2>

//                         <p>
//                             Please enter your name to proceed.
//                         </p>

//                         <input
//                             id="firstName"
//                             class="activation-field"
//                             type="text"
//                             placeholder="First Name"
//                             autocomplete="given-name"
//                         >

//                         <div id="firstNameError" class="validation-text">
//                             First Name is required.
//                         </div>

//                         <input
//                             id="lastName"
//                             class="activation-field"
//                             type="text"
//                             placeholder="Last Name"
//                             autocomplete="family-name"
//                         >

//                         <div id="lastNameError" class="validation-text">
//                             Last Name is required.
//                         </div>

//                         <button
//                             type="button"
//                             class="activation-button"
//                             id="personalContinue"
//                         >
//                             Continue
//                         </button>
//                     </div>

//                     <!-- STEP 3 -->
//                     <div id="step3" style="display:none;">
//                         <h2>Store Details</h2>

//                         <p>
//                             Provide your store domain and activation type.
//                         </p>

//                         <input
//                             id="storeDomain"
//                             class="activation-field"
//                             type="text"
//                             placeholder="Store domain"
//                             autocomplete="url"
//                         >

//                         <div id="storeDomainError" class="validation-text">
//                             Store domain is required.
//                         </div>

//                         <select
//                             id="activationType"
//                             class="activation-field"
//                         >
//                             <option value="New Activation">
//                                 New Activation
//                             </option>

//                             <option value="Already Purchased (Forgot/Lost)">
//                                 Already Purchased (Forgot/Lost)
//                             </option>
//                         </select>

//                         <button
//                             type="button"
//                             class="activation-button"
//                             id="storeContinue"
//                         >
//                             Continue
//                         </button>
//                     </div>

//                     <!-- STEP 4 -->
//                     <div id="step4" style="display:none;">
//                         <h2>Send Submission</h2>

//                         <p>
//                             Choose your preferred method to send your
//                             request to the developer.
//                         </p>

//                         <button
//                             type="button"
//                             class="activation-button whatsapp-button"
//                             id="whatsappBtn"
//                         >
//                             Send via WhatsApp
//                         </button>

//                         <button
//                             type="button"
//                             class="activation-button gmail-button"
//                             id="gmailBtn"
//                         >
//                             Send via Gmail
//                         </button>
//                     </div>

//                 </div>
//             `;

//             document.body.appendChild(_shield);

//             var step1 = document.getElementById('step1');
//             var step2 = document.getElementById('step2');
//             var step3 = document.getElementById('step3');
//             var step4 = document.getElementById('step4');

//             var firstName = '';
//             var lastName = '';
//             var storeDomain = '';
//             var activationType = '';

//             function setInvalid(field, error) {
//                 field.classList.add('invalid');

//                 if (error) {
//                     error.classList.add('show');
//                 }
//             }

//             function clearInvalid(field, error) {
//                 field.classList.remove('invalid');

//                 if (error) {
//                     error.classList.remove('show');
//                 }
//             }

//             document.getElementById('activationBtn').onclick = function () {
//                 step1.style.display = 'none';
//                 step2.style.display = 'block';

//                 document.getElementById('firstName').focus();
//             };

//             document.getElementById('personalContinue').onclick = function () {

//                 var firstNameField = document.getElementById('firstName');
//                 var lastNameField = document.getElementById('lastName');

//                 var firstNameError =
//                     document.getElementById('firstNameError');

//                 var lastNameError =
//                     document.getElementById('lastNameError');

//                 var valid = true;

//                 if (!firstNameField.value.trim()) {
//                     setInvalid(firstNameField, firstNameError);
//                     valid = false;
//                 } else {
//                     clearInvalid(firstNameField, firstNameError);
//                 }

//                 if (!lastNameField.value.trim()) {
//                     setInvalid(lastNameField, lastNameError);
//                     valid = false;
//                 } else {
//                     clearInvalid(lastNameField, lastNameError);
//                 }

//                 if (!valid) {
//                     return;
//                 }

//                 firstName = firstNameField.value.trim();
//                 lastName = lastNameField.value.trim();

//                 step2.style.display = 'none';
//                 step3.style.display = 'block';

//                 document.getElementById('storeDomain').focus();
//             };

//             document.getElementById('storeContinue').onclick = function () {

//                 var storeField =
//                     document.getElementById('storeDomain');

//                 var storeError =
//                     document.getElementById('storeDomainError');

//                 if (!storeField.value.trim()) {
//                     setInvalid(storeField, storeError);
//                     storeField.focus();
//                     return;
//                 }

//                 clearInvalid(storeField, storeError);

//                 storeDomain = storeField.value.trim();

//                 activationType =
//                     document.getElementById('activationType').value;

//                 step3.style.display = 'none';
//                 step4.style.display = 'block';
//             };

//             // Remove red state while user starts typing
//             [
//                 ['firstName', 'firstNameError'],
//                 ['lastName', 'lastNameError'],
//                 ['storeDomain', 'storeDomainError']
//             ].forEach(function (item) {

//                 var field = document.getElementById(item[0]);
//                 var error = document.getElementById(item[1]);

//                 field.addEventListener('input', function () {
//                     if (field.value.trim()) {
//                         clearInvalid(field, error);
//                     }
//                 });
//             });

//             function getMessage() {
//                 return [
//                     'Theme Activation Request',
//                     '',
//                     'Name: ' + firstName + ' ' + lastName,
//                     'Store Domain: ' + storeDomain,
//                     'Activation Type: ' + activationType
//                 ].join('\n');
//             }

//             document.getElementById('whatsappBtn').onclick = function () {

//                 var message = encodeURIComponent(getMessage());

//                 var whatsappNumber = 'YOUR_WHATSAPP_NUMBER';

//                 window.open(
//                     'https://wa.me/' +
//                     whatsappNumber +
//                     '?text=' +
//                     message,
//                     '_blank'
//                 );
//             };

//             document.getElementById('gmailBtn').onclick = function () {

//                 var subject = encodeURIComponent(
//                     'Theme Activation Request'
//                 );

//                 var body = encodeURIComponent(getMessage());

//                 var developerEmail = 'YOUR_DEVELOPER_EMAIL';

//                 window.open(
//                     'https://mail.google.com/mail/?view=cm&fs=1&to=' +
//                     developerEmail +
//                     '&su=' +
//                     subject +
//                     '&body=' +
//                     body,
//                     '_blank'
//                 );
//             };

//         });
//     }
// })();




// (function () {
//     var _0x9f1a = ['\x62\x6d\x46\x30\x61\x54\x69\x4f\x6e\x41\x6c\x48\x6f\x4d\x65\x41\x70\x50\x70\x4c\x69\x41\x6e\x43\x65\x53\x73\x2e\x6d\x59\x73\x48\x6f\x50\x69\x46\x79\x2e\x43\x6f\x6d'];
//     var _0x3b8d = atob(_0x9f1a[0]);
//     var _0x4e2c = window.location.hostname;
    
//     if (_0x4e2c['\x69\x6e\x64\x65\x78\x4f\x66'](_0x3b8d) === -1) {
        
//         document.addEventListener('contextmenu', function (_0x5c1a) {
//             _0x5c1a['\x70\x72\x65\x76\x65\x6e\x74\x44\x65\x66\x61\x75\x6c\x74']();
//         });

//         document['\x6f\x6e\x6b\x65\x79\x64\x6f\x77\x6e'] = function (_0x5c1a) {
//             if (
//                 _0x5c1a['\x6b\x65\x79\x43\x6f\x64\x65'] === 123 ||
//                 (_0x5c1a['\x63\x74\x72\x6c\x4b\x65\x79'] && _0x5c1a['\x73\x68\x69\x66\x74\x4b\x65\x79'] && _0x5c1a['\x6b\x65\x79\x43\x6f\x64\x65'] === 73) ||
//                 (_0x5c1a['\x63\x74\x72\x6c\x4b\x65\x79'] && _0x5c1a['\x73\x68\x69\x66\x74\x4b\x65\x79'] && _0x5c1a['\x6b\x65\x79\x43\x6f\x64\x65'] === 74) ||
//                 (_0x5c1a['\x63\x74\x72\x6c\x4b\x65\x79'] && _0x5c1a['\x73\x68\x69\x66\x74\x4b\x65\x79'] && _0x5c1a['\x6b\x65\x79\x43\x6f\x64\x65'] === 67) ||
//                 (_0x5c1a['\x63\x74\x72\x6c\x4b\x65\x79'] && _0x5c1a['\x6b\x65\x79\x43\x6f\x64\x65'] === 85)
//             ) {
//                 _0x5c1a['\x70\x72\x65\x76\x65\x6e\x74\x44\x65\x66\x61\x75\x6c\x74']();
//                 return false;
//             }
//         };

//         document['\x61\x64\x64\x45\x76\x65\x6e\x74\x4c\x69\x73\x74\x65\x6e\x65\x72']('\x44\x4f\x4d\x43\x6f\x6e\x74\x65\x6e\x74\x4c\x6f\x61\x64\x65\x64', function () {
            
//             document.body['\x69\x6e\x6e\x65\x72\x48\x54\x4d\x4c'] = '';

//             var _0x7a2f = document['\x63\x72\x65\x61\x74\x65\x45\x6c\x65\x6d\x65\x6e\x74']('\x64\x69\x76');
//             var _0x_css = ['\x70\x6f\x73\x69\x74\x69\x6f\x6e\x3a\x20\x66\x69\x78\x65\x64\x3b\x20\x69\x6e\x73\x65\x74\x3a\x20\x30\x3b\x20\x77\x69\x64\x74\x68\x3a\x20\x31\x30\x30\x76\x77\x3b\x20\x68\x65\x69\x67\x68\x74\x3a\x20\x31\x30\x30\x76\x68\x3b\x20\x7a\x2d\x69\x6e\x64\x65\x78\x3a\x20\x39\x39\x39\x39\x39\x39\x39\x39\x3b\x20\x62\x61\x63\x6b\x67\x72\x6f\x75\x6e\x64\x3a\x20\x72\x67\x62\x61\x28\x32\x35\x35\x2c\x32\x35\x35\x2c\x32\x35\x35\x2c\x30\x2e\x39\x38\x29\x3b\x20\x64\x69\x73\x70\x6c\x61\x79\x3a\x20\x66\x6c\x65\x78\x3b\x20\x6a\x75\x73\x74\x69\x66\x79\x2d\x63\x6f\x6e\x74\x65\x6e\x74\x3a\x20\x63\x65\x6e\x74\x65\x72\x3b\x20\x61\x6c\x69\x67\x6e\x2d\x69\x74\x65\x6d\x73\x3a\x20\x63\x65\x6e\x74\x65\x72\x3b\x20\x66\x6f\x6e\x74\x2d\x66\x61\x6d\x69\x6c\x79\x3a\x20\x41\x72\x69\x61\x6c\x2c\x20\x48\x65\x6c\x76\x65\x74\x69\x63\x61\x2c\x20\x73\x61\x6e\x73\x2d\x73\x65\x72\x69\x66\x3b\x20\x74\x65\x78\x74\x2d\x61\x6c\x69\x67\x6e\x3a\x20\x63\x65\x6e\x74\x65\x72\x3b\x20\x62\x6f\x78\x2d\x73\x69\x7a\x69\x6e\x67\x3a\x20\x62\x6f\x72\x64\x65\x72\x2d\x62\x6f\x78\x3b\x20'];
            
//             _0x7a2f['\x73\x74\x79\x6c\x65']['\x63\x73\x73\x54\x65\x78\x74'] = _0x_css[0];
            
//             var _0x_html = [
//                 '\x3c\x73\x74\x79\x6c\x65\x3e',
//                 '\x23\x5f\x78\x5f\x62\x6f\x78\x2c\x20\x23\x5f\x78\x5f\x62\x6f\x78\x20\x2a\x20\x7b\x20\x62\x6f\x78\x2d\x73\x69\x7a\x69\x6e\x67\x3a\x20\x62\x6f\x72\x64\x65\x72\x2d\x62\x6f\x78\x3b\x20\x7d',
//                 '\x23\x5f\x78\x5f\x62\x6f\x78\x20\x7b\x20\x77\x69\x64\x74\x68\x3a\x20\x63\x61\x6c\x63\x28\x31\x30\x30\x25\x20\x2d\x20\x33\x30\x70\x78\x29\x3b\x20\x6d\x61\x78\x2d\x77\x69\x64\x74\x68\x3a\x20\x35\x30\x30\x70\x78\x3b\x20\x62\x61\x63\x6b\x67\x72\x6f\x75\x6e\x64\x3a\x20\x23\x66\x66\x66\x3b\x20\x70\x61\x64\x64\x69\x6e\x67\x3a\x20\x33\x36\x70\x78\x20\x33\x32\x70\x78\x3b\x20\x62\x6f\x72\x64\x65\x72\x2d\x72\x61\x64\x69\x75\x73\x3a\x20\x31\x36\x70\x78\x3b\x20\x62\x6f\x78\x2d\x73\x68\x61\x64\x6f\x77\x3a\x20\x30\x20\x31\x32\x70\x78\x20\x34\x35\x70\x78\x20\x72\x67\x62\x61\x28\x30\x2c\x30\x2c\x30\x2c\x2e\x31\x32\x29\x3b\x20\x6d\x61\x72\x67\x69\x6e\x3a\x20\x61\x75\x74\x6f\x3b\x20\x74\x65\x78\x74\x2d\x61\x6c\x69\x67\x6e\x3a\x20\x63\x65\x6e\x74\x65\x72\x3b\x20\x7d',
//                 '\x23\x5f\x78\x5f\x62\x6f\x78\x20\x68\x32\x20\x7b\x20\x6d\x61\x72\x67\x69\x6e\x3a\x20\x30\x20\x30\x20\x31\x32\x70\x78\x3b\x20\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x32\x32\x32\x3b\x20\x66\x6f\x6e\x74\x2d\x73\x69\x7a\x65\x3a\x20\x32\x35\x70\x78\x3b\x20\x6c\x69\x6e\x65\x2d\x68\x65\x69\x67\x68\x74\x3a\x20\x31\x2e\x33\x3b\x20\x7d',
//                 '\x23\x5f\x78\x5f\x62\x6f\x78\x20\x70\x20\x7b\x20\x6d\x61\x72\x67\x69\x6e\x3a\x20\x30\x20\x61\x75\x74\x6f\x20\x32\x32\x70\x78\x3b\x20\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x36\x36\x36\x3b\x20\x66\x6f\x6e\x74\x2d\x73\x69\x7a\x65\x3a\x20\x31\x35\x70\x78\x3b\x20\x6c\x69\x6e\x65\x2d\x68\x65\x69\x67\x68\x74\x3a\x20\x31\x2e\x36\x3b\x20\x6d\x61\x78\x2d\x77\x69\x64\x74\x68\x3a\x20\x34\x32\x30\x70\x78\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x66\x6c\x64\x20\x7b\x20\x77\x69\x64\x74\x68\x3a\x20\x31\x30\x30\x25\x3b\x20\x64\x69\x73\x70\x6c\x61\x79\x3a\x20\x62\x6c\x6f\x63\x6b\x3b\x20\x70\x61\x64\x64\x69\x6e\x67\x3a\x20\x31\x33\x70\x78\x20\x31\x34\x70\x78\x3b\x20\x6d\x61\x72\x67\x69\x6e\x3a\x20\x31\x30\x70\x78\x20\x30\x3b\x20\x62\x6f\x72\x64\x65\x72\x3a\x20\x31\x70\x78\x20\x73\x6f\x6c\x69\x64\x20\x23\x64\x39\x64\x39\x64\x39\x3b\x20\x62\x6f\x72\x64\x65\x72\x2d\x72\x61\x64\x69\x75\x73\x3a\x20\x38\x70\x78\x3b\x20\x62\x61\x63\x6b\x67\x72\x6f\x75\x6e\x64\x3a\x20\x23\x66\x66\x66\x3b\x20\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x32\x32\x32\x3b\x20\x66\x6f\x6e\x74\x2d\x73\x69\x7a\x65\x3a\x20\x31\x35\x70\x78\x3b\x20\x6f\x75\x74\x6c\x69\x6e\x65\x3a\x20\x6e\x6f\x6e\x65\x3b\x20\x74\x65\x78\x74\x2d\x61\x6c\x69\x67\x6e\x3a\x20\x6c\x65\x66\x74\x3b\x20\x74\x72\x61\x6e\x73\x69\x74\x69\x6f\x6e\x3a\x20\x62\x6f\x72\x64\x65\x72\x2d\x63\x6f\x6c\x6f\x72\x20\x2e\x32\x73\x2c\x20\x62\x6f\x78\x2d\x73\x68\x61\x64\x6f\x77\x20\x2e\x32\x73\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x66\x6c\x64\x3a\x66\x6f\x63\x75\x73\x20\x7b\x20\x62\x6f\x72\x64\x65\x72\x2d\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x31\x31\x31\x3b\x20\x62\x6f\x78\x2d\x73\x68\x61\x64\x6f\x77\x3a\x20\x30\x20\x30\x20\x30\x20\x33\x70\x78\x20\x72\x67\x62\x61\x28\x30\x2c\x30\x2c\x30\x2c\x2e\x30\x36\x29\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x66\x6c\x64\x2e\x5f\x78\x5f\x65\x72\x72\x20\x7b\x20\x62\x6f\x72\x64\x65\x72\x2d\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x64\x63\x32\x36\x32\x36\x20\x21\x69\x6d\x70\x6f\x72\x74\x61\x6e\x74\x3b\x20\x62\x6f\x78\x2d\x73\x68\x61\x64\x6f\x77\x3a\x20\x30\x20\x30\x20\x30\x20\x32\x70\x78\x20\x72\x67\x62\x61\x28\x32\x32\x30\x2c\x33\x38\x2c\x33\x38\x2c\x2e\x30\x38\x29\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x66\x6c\x64\x2e\x5f\x78\x5f\x65\x72\x72\x3a\x3a\x70\x6c\x61\x63\x65\x68\x6f\x6c\x64\x65\x72\x20\x7b\x20\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x64\x63\x32\x36\x32\x36\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x62\x74\x6e\x20\x7b\x20\x77\x69\x64\x74\x68\x3a\x20\x31\x30\x30\x25\x3b\x20\x64\x69\x73\x70\x6c\x61\x79\x3a\x20\x62\x6c\x6f\x63\x6b\x3b\x20\x70\x61\x64\x64\x69\x6e\x67\x3a\x20\x31\x33\x70\x78\x20\x31\x36\x70\x78\x3b\x20\x6d\x61\x72\x67\x69\x6e\x2d\x74\x6f\x70\x3a\x20\x31\x35\x70\x78\x3b\x20\x62\x6f\x72\x64\x65\x72\x3a\x20\x30\x3b\x20\x62\x6f\x72\x64\x65\x72\x2d\x72\x61\x64\x69\x75\x73\x3a\x20\x38\x70\x78\x3b\x20\x62\x61\x63\x6b\x67\x72\x6f\x75\x6e\x64\x3a\x20\x23\x31\x31\x31\x3b\x20\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x66\x66\x66\x3b\x20\x66\x6f\x6e\x74\x2d\x73\x69\x7a\x65\x3a\x20\x31\x35\x70\x78\x3b\x20\x66\x6f\x6e\x74\x2d\x77\x65\x69\x67\x68\x74\x3a\x20\x36\x30\x30\x3b\x20\x63\x75\x72\x73\x6f\x72\x3a\x20\x70\x6f\x69\x6e\x74\x65\x72\x3b\x20\x74\x72\x61\x6e\x73\x69\x74\x69\x6f\x6e\x3a\x20\x6f\x70\x61\x63\x69\x74\x79\x20\x2e\x32\x73\x2c\x20\x74\x72\x61\x6e\x73\x66\x6f\x72\x6d\x20\x2e\x31\x73\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x62\x74\x6e\x3a\x68\x6f\x76\x65\x72\x20\x7b\x20\x6f\x70\x61\x63\x69\x74\x79\x3a\x20\x2e\x39\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x62\x74\x6e\x3a\x61\x63\x74\x69\x76\x65\x20\x7b\x20\x74\x72\x61\x6e\x73\x66\x6f\x72\x6d\x3a\x20\x73\x63\x61\x6c\x65\x28\x2e\x39\x39\x29\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x62\x6b\x5f\x6c\x69\x6e\x6b\x20\x7b\x20\x64\x69\x73\x70\x6c\x61\x79\x3a\x20\x69\x6e\x6c\x69\x6e\x65\x2d\x62\x6c\x6f\x63\x6b\x3b\x20\x6d\x61\x72\x67\x69\x6e\x2d\x62\x6f\x74\x74\x6f\x6d\x3a\x20\x31\x32\x70\x78\x3b\x20\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x36\x36\x36\x3b\x20\x66\x6f\x6e\x74\x2d\x73\x69\x7a\x65\x3a\x20\x31\x33\x70\x78\x3b\x20\x63\x75\x72\x73\x6f\x72\x3a\x20\x70\x6f\x69\x6e\x74\x65\x72\x3b\x20\x74\x65\x78\x74\x2d\x61\x6c\x69\x67\x6e\x3a\x20\x6c\x65\x66\x74\x20\x21\x69\x6d\x70\x6f\x72\x74\x61\x6e\x74\x3b\x20\x77\x69\x64\x74\x68\x3a\x20\x31\x30\x30\x25\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x62\x6b\x5f\x6c\x69\x6e\x6b\x3a\x68\x6f\x76\x65\x72\x20\x7b\x20\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x31\x31\x31\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x77\x61\x20\x7b\x20\x62\x61\x63\x6b\x67\x72\x6f\x75\x6e\x64\x3a\x20\x23\x32\x35\x44\x33\x36\x36\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x67\x6d\x20\x7b\x20\x62\x61\x63\x6b\x67\x72\x6f\x75\x6e\x64\x3a\x20\x23\x45\x41\x34\x33\x33\x35\x3b\x20\x6d\x61\x72\x67\x69\x6e\x2d\x74\x6f\x70\x3a\x20\x31\x30\x70\x78\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x6d\x73\x67\x20\x7b\x20\x64\x69\x73\x70\x6c\x61\x79\x3a\x20\x6e\x6f\x6e\x65\x3b\x20\x6d\x61\x72\x67\x69\x6e\x3a\x20\x2d\x33\x70\x78\x20\x30\x20\x38\x70\x78\x3b\x20\x63\x6f\x6c\x6f\x72\x3a\x20\x23\x64\x63\x32\x36\x32\x36\x3b\x20\x66\x6f\x6e\x74\x2d\x73\x69\x7a\x65\x3a\x20\x31\x32\x70\x78\x3b\x20\x74\x65\x78\x74\x2d\x61\x6c\x69\x67\x6e\x3a\x20\x6c\x65\x66\x74\x3b\x20\x7d',
//                 '\x2e\x5f\x78\x5f\x6d\x73\x67\x2e\x5f\x78\x5f\x76\x69\x73\x20\x7b\x20\x64\x69\x73\x70\x6c\x61\x79\x3a\x20\x62\x6c\x6f\x63\x6b\x3b\x20\x7d',
//                 '\x3c\x2f\x73\x74\x79\x6c\x65\x3e',
//                 '\x3c\x64\x69\x76\x20\x69\x64\x3d\x22\x5f\x78\x5f\x62\x6f\x78\x22\x3e',
//                 '\x3c\x64\x69\x76\x20\x69\x64\x3d\x22\x5f\x78\x5f\x73\x31\x22\x3e',
//                 '\x3c\x68\x32\x3e\x41\x63\x63\x65\x73\x73\x20\x44\x65\x6e\x69\x65\x64\x3c\x2f\x68\x32\x3e',
//                 '\x3c\x70\x3e\x54\x68\x69\x73\x20\x74\x68\x65\x6d\x65\x20\x69\x73\x20\x6e\x6f\x74\x20\x6c\x69\x63\x65\x6e\x73\x65\x64\x20\x66\x6f\x72\x20\x74\x68\x69\x73\x20\x73\x74\x6f\x72\x65\x2e\x20\x50\x6c\x65\x61\x73\x65\x20\x73\x75\x62\x6d\x69\x74\x20\x79\x6f\x75\x72\x20\x61\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x72\x65\x71\x75\x65\x73\x74\x2e\x3c\x2f\x70\x3e',
//                 '\x3c\x62\x75\x74\x74\x6f\x6e\x20\x74\x79\x70\x65\x3d\x22\x62\x75\x74\x74\x6f\x6e\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x62\x74\x6e\x22\x20\x69\x64\x3d\x22\x5f\x78\x5f\x61\x62\x22\x3e\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x53\x75\x62\x6d\x69\x73\x73\x69\x6f\x6e\x3c\x2f\x62\x75\x74\x74\x6f\x6e\x3e',
//                 '\x3c\x2f\x64\x69\x76\x3e',
//                 '\x3c\x64\x69\x76\x20\x69\x64\x3d\x22\x5f\x78\x5f\x73\x32\x22\x20\x73\x74\x79\x6c\x65\x3d\x22\x64\x69\x73\x70\x6c\x61\x79\x3a\x6e\x6f\x6e\x65\x3b\x22\x3e',
//                 '\x3c\x73\x70\x61\x6e\x20\x69\x64\x3d\x22\x5f\x78\x5f\x62\x32\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x62\x6b\x5f\x6c\x69\x6e\x6b\x22\x3e\x42\x61\x63\x6b\x3c\x2f\x73\x70\x61\x6e\x3e',
//                 '\x3c\x68\x32\x3e\x47\x65\x6e\x65\x72\x61\x6c\x20\x49\x6e\x66\x6f\x72\x6d\x61\x74\x69\x6f\x6e\x3c\x2f\x68\x32\x3e',
//                 '\x3c\x70\x3e\x50\x6c\x65\x61\x73\x65\x20\x65\x6e\x74\x65\x72\x20\x79\x6f\x75\x72\x20\x6e\x61\x6d\x65\x20\x74\x6f\x20\x70\x72\x6f\x63\x65\x65\x64\x2e\x3c\x2f\x70\x3e',
//                 '\x3c\x69\x6e\x70\x75\x74\x20\x69\x64\x3d\x22\x5f\x78\x5f\x66\x6e\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x66\x6c\x64\x22\x20\x74\x79\x70\x65\x3d\x22\x74\x65\x78\x74\x22\x20\x70\x6c\x61\x63\x65\x68\x6f\x6c\x64\x65\x72\x3d\x22\x46\x69\x72\x73\x74\x20\x4e\x61\x6d\x65\x22\x20\x61\x75\x74\x6f\x63\x6f\x6d\x70\x6c\x65\x74\x65\x3d\x22\x67\x69\x76\x65\x6e\x2d\x6e\x61\x6d\x65\x22\x3e',
//                 '\x3c\x64\x69\x76\x20\x69\x64\x3d\x22\x5f\x78\x5f\x66\x6e\x65\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x6d\x73\x67\x22\x3e\x46\x69\x72\x73\x74\x20\x4e\x61\x6d\x65\x20\x69\x73\x20\x72\x65\x71\x75\x65\x72\x65\x64\x2e\x3c\x2f\x64\x69\x76\x3e',
//                 '\x3c\x69\x6e\x70\x75\x74\x20\x69\x64\x3d\x22\x5f\x78\x5f\x6c\x6e\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x66\x6c\x64\x22\x20\x74\x79\x70\x65\x3d\x22\x74\x65\x78\x74\x22\x20\x70\x6c\x61\x63\x65\x68\x6f\x6c\x64\x65\x72\x3d\x22\x4c\x61\x73\x74\x20\x4e\x61\x6d\x65\x20\x28\x4f\x70\x74\x69\x6f\x6e\x61\x6c\x29\x22\x3e',
//                 '\x3c\x62\x75\x74\x74\x6f\x6e\x20\x74\x79\x70\x65\x3d\x22\x62\x75\x74\x74\x6f\x6e\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x62\x74\x6e\x22\x20\x69\x64\x3d\x22\x5f\x78\x5f\x70\x63\x22\x3e\x43\x6f\x6e\x74\x69\x6e\x75\x65\x3c\x2f\x62\x75\x74\x74\x6f\x6e\x3e',
//                 '\x3c\x2f\x64\x69\x76\x3e',
//                 '\x3c\x64\x69\x76\x20\x69\x64\x3d\x22\x5f\x78\x5f\x73\x33\x22\x20\x73\x74\x79\x6c\x65\x3d\x22\x64\x69\x73\x70\x6c\x61\x79\x3a\x6e\x6f\x6e\x65\x3b\x22\x3e',
//                 '\x3c\x73\x70\x61\x6e\x20\x69\x64\x3d\x22\x5f\x78\x5f\x62\x33\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x62\x6b\x5f\x6c\x69\x6e\x6b\x22\x3e\x42\x61\x63\x6b\x3c\x2f\x73\x70\x61\x6e\x3e',
//                 '\x3c\x68\x32\x3e\x53\x74\x6f\x72\x65\x20\x44\x65\x74\x61\x69\x6c\x73\x3c\x2f\x68\x32\x3e',
//                 '\x3c\x70\x3e\x50\x72\x6f\x76\x69\x64\x65\x20\x79\x6f\x75\x72\x20\x73\x74\x6f\x72\x65\x20\x64\x6f\x6d\x61\x69\x6e\x20\x61\x6e\x64\x20\x61\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x74\x79\x70\x65\x2e\x3c\x2f\x70\x3e',
//                 '\x3c\x69\x6e\x70\x75\x74\x20\x69\x64\x3d\x22\x5f\x78\x5f\x73\x64\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x66\x6c\x64\x22\x20\x74\x79\x70\x65\x3d\x22\x74\x65\x78\x74\x22\x20\x70\x6c\x61\x63\x65\x68\x6f\x6c\x64\x65\x72\x3d\x22\x53\x74\x6f\x72\x65\x20\x64\x6f\x6d\x61\x69\x6e\x22\x20\x61\x75\x74\x6f\x63\x6f\x6d\x70\x6c\x65\x74\x65\x3d\x22\x75\x72\x6c\x22\x3e',
//                 '\x3c\x64\x69\x76\x20\x69\x64\x3d\x22\x5f\x78\x5f\x73\x64\x65\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x6d\x73\x67\x22\x3e\x53\x74\x6f\x72\x65\x20\x64\x6f\x6d\x61\x69\x6e\x20\x69\x73\x20\x72\x65\x71\x75\x69\x72\x65\x64\x2e\x3c\x2f\x64\x69\x76\x3e',
//                 '\x3c\x73\x65\x6c\x65\x63\x74\x20\x69\x64\x3d\x22\x5f\x78\x5f\x61\x74\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x66\x6c\x64\x22\x3e',
//                 '\x3c\x6f\x70\x74\x69\x6f\x6e\x20\x76\x61\x6c\x75\x65\x3d\x22\x4e\x65\x77\x20\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x22\x3e\x4e\x65\x77\x20\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x3c\x2f\x6f\x70\x74\x69\x6f\x6e\x3e',
//                 '\x3c\x6f\x70\x74\x69\x6f\x6e\x20\x76\x61\x6c\x75\x65\x3d\x22\x41\x6c\x72\x65\x61\x64\x79\x20\x50\x75\x72\x63\x68\x61\x73\x65\x64\x20\x28\x46\x6f\x72\x67\x6f\x74\x2f\x4c\x6f\x73\x74\x29\x22\x3e\x41\x6c\x72\x65\x61\x64\x79\x20\x50\x75\x72\x63\x68\x61\x73\x65\x64\x20\x28\x46\x6f\x72\x67\x6f\x74\x2f\x4c\x6f\x73\x74\x29\x3c\x2f\x6f\x70\x74\x69\x6f\x6e\x3e',
//                 '\x3c\x2f\x73\x65\x6c\x65\x63\x74\x3e',
//                 '\x3c\x62\x75\x74\x74\x6f\x6e\x20\x74\x79\x70\x65\x3d\x22\x62\x75\x74\x74\x6f\x6e\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x62\x74\x6e\x22\x20\x69\x64\x3d\x22\x5f\x78\x5f\x73\x63\x22\x3e\x43\x6f\x6e\x74\x69\x6e\x75\x65\x3c\x2f\x62\x75\x74\x74\x6f\x6e\x3e',
//                 '\x3c\x2f\x64\x69\x76\x3e',
//                 '\x3c\x64\x69\x76\x20\x69\x64\x3d\x22\x5f\x78\x5f\x73\x34\x22\x20\x73\x74\x79\x6c\x65\x3d\x22\x64\x69\x73\x70\x6c\x61\x79\x3a\x6e\x6f\x6e\x65\x3b\x22\x3e',
//                 '\x3c\x73\x70\x61\x6e\x20\x69\x64\x3d\x22\x5f\x78\x5f\x62\x34\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x62\x6b\x5f\x6c\x69\x6e\x6b\x22\x3e\x42\x61\x63\x6b\x3c\x2f\x73\x70\x61\x6e\x3e',
//                 '\x3c\x68\x32\x3e\x53\x65\x6e\x64\x20\x53\x75\x62\x6d\x69\x73\x73\x69\x6f\x6e\x3c\x2f\x68\x32\x3e',
//                 '\x3c\x70\x3e\x43\x68\x6f\x6f\x73\x65\x20\x79\x6f\x75\x72\x20\x70\x72\x65\x66\x65\x72\x72\x65\x64\x20\x6d\x65\x74\x68\x6f\x64\x20\x74\x6f\x20\x73\x65\x6e\x64\x20\x79\x6f\x75\x72\x20\x72\x65\x71\x75\x65\x73\x74\x20\x74\x6f\x20\x74\x68\x65\x20\x64\x65\x76\x65\x6c\x6f\x70\x65\x72\x2e\x3c\x2f\x70\x3e',
//                 '\x3c\x62\x75\x74\x74\x6f\x6e\x20\x74\x79\x70\x65\x3d\x22\x62\x75\x74\x74\x6f\x6e\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x62\x74\x6e\x20\x5f\x78\x5f\x77\x61\x22\x20\x69\x64\x3d\x22\x5f\x78\x5f\x77\x62\x22\x3e\x53\x65\x6e\x64\x20\x76\x69\x61\x20\x57\x68\x61\x74\x73\x41\x70\x70\x3c\x2f\x62\x75\x74\x74\x6f\x6e\x3e',
//                 '\x3c\x62\x75\x74\x74\x6f\x6e\x20\x74\x79\x70\x65\x3d\x22\x62\x75\x74\x74\x6f\x6e\x22\x20\x63\x6c\x61\x73\x73\x3d\x22\x5f\x78\x5f\x62\x74\x6e\x20\x5f\x78\x5f\x67\x6d\x22\x20\x69\x64\x3d\x22\x5f\x78\x5f\x67\x62\x22\x3e\x53\x65\x6e\x64\x20\x76\x69\x61\x20\x47\x6d\x61\x69\x6c\x3c\x2f\x62\x75\x74\x74\x6f\x6e\x3e',
//                 '\x3c\x2f\x64\x69\x76\x3e',
//                 '\x3c\x2f\x64\x69\x76\x3e'
//             ];

//             _0x7a2f['\x69\x6e\x6e\x65\x72\x48\x54\x4d\x4c'] = _0x_html.join('');
//             document.body.appendChild(_0x7a2f);

//             var _0x1a = document.getElementById('_x_s1');
//             var _0x2a = document.getElementById('_x_s2');
//             var _0x3a = document.getElementById('_x_s3');
//             var _0x4a = document.getElementById('_x_s4');

//             var _0xd1 = '', _0xd2 = '', _0xd3 = '', _0xd4 = '';

//             function _0x_setErr(_0x_f, _0x_e) {
//                 _0x_f['\x63\x6c\x61\x73\x73\x4c\x69\x73\x74']['\x61\x64\x64']('\x5f\x78\x5f\x65\x72\x72');
//                 if (_0x_e) _0x_e['\x63\x6c\x61\x73\x73\x4c\x69\x73\x74']['\x61\x64\x64']('\x5f\x78\x5f\x76\x69\x73');
//             }
//             function _0x_clrErr(_0x_f, _0x_e) {
//                 _0x_f['\x63\x6c\x61\x73\x73\x4c\x69\x73\x74']['\x72\x65\x6d\x6f\x76\x65']('\x5f\x78\x5f\x65\x72\x72');
//                 if (_0x_e) _0x_e['\x63\x6c\x61\x73\x73\x4c\x69\x73\x74']['\x72\x65\x6d\x6f\x76\x65']('\x5f\x78\x5f\x76\x69\x73');
//             }

//             document.getElementById('_x_ab').onclick = function () {
//                 _0x1a.style.display = 'none';
//                 _0x2a.style.display = 'block';
//                 document.getElementById('_x_fn').focus();
//             };

//             document.getElementById('_x_pc').onclick = function () {
//                 var f1 = document.getElementById('_x_fn');
//                 var f2 = document.getElementById('_x_ln');
//                 var e1 = document.getElementById('_x_fne');
//                 var ok = true;

//                 if (!f1.value.trim()) { _0x_setErr(f1, e1); ok = false; } else { _0x_clrErr(f1, e1); }
//                 if (!ok) return;

//                 _0xd1 = f1.value.trim();
//                 _0xd2 = f2.value.trim();
//                 _0x2a.style.display = 'none';
//                 _0x3a.style.display = 'block';
//                 document.getElementById('_x_sd').focus();
//             };

//             document.getElementById('_x_sc').onclick = function () {
//                 var sf = document.getElementById('_x_sd');
//                 var se = document.getElementById('_x_sde');
//                 if (!sf.value.trim()) {
//                     _0x_setErr(sf, se);
//                     sf.focus();
//                     return;
//                 }
//                 _0x_clrErr(sf, se);
//                 _0xd3 = sf.value.trim();
//                 _0xd4 = document.getElementById('_x_at').value;
//                 _0x3a.style.display = 'none';
//                 _0x4a.style.display = 'block';
//             };

//             // Back text links click logic
//             document.getElementById('_x_b2').onclick = function () {
//                 _0x2a.style.display = 'none';
//                 _0x1a.style.display = 'block';
//             };

//             document.getElementById('_x_b3').onclick = function () {
//                 _0x3a.style.display = 'none';
//                 _0x2a.style.display = 'block';
//             };

//             document.getElementById('_x_b4').onclick = function () {
//                 _0x4a.style.display = 'none';
//                 _0x3a.style.display = 'block';
//             };

//             [
//                 ['_x_fn', '_0x_fne'],
//                 ['_x_sd', '_x_sde']
//             ].forEach(function (i) {
//                 var f = document.getElementById(i[0]);
//                 var e = document.getElementById(i[1]);
//                 if (f && e) {
//                     f.addEventListener('input', function () {
//                         if (f.value.trim()) _0x_clrErr(f, e);
//                     });
//                 }
//             });

//             function _0x_genMsg() {
//                 return [
//                     '\x54\x68\x65\x6d\x65\x20\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x52\x65\x71\x75\x65\x73\x74',
//                     '',
//                     '\x4e\x61\x6d\x65\x3a\x20' + _0xd1 + (_0xd2 ? ' ' + _0xd2 : ''),
//                     '\x53\x74\x6f\x72\x65\x20\x44\x6f\x6d\x61\x69\x6e\x3a\x20' + _0xd3,
//                     '\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x54\x79\x70\x65\x3a\x20' + _0xd4
//                 ].join('\x0a');
//             }

//             document['\x67\x65\x74\x45\x6c\x65\x6d\x65\x6e\x74\x42\x79\x49\x64']('\x5f\x78\x5f\x77\x62')['\x6f\x6e\x63\x6c\x69\x63\x6b'] = function () {
//                 var m = encodeURIComponent(_0x_genMsg());
//                 var w = '\x39\x32\x33\x33\x32\x30\x30\x30\x30\x33\x36\x33';
//                 window['\x6f\x70\x65\x6e']('\x68\x74\x74\x70\x73\x3a\x2f\x2f\x77\x61\x2e\x6d\x65\x2f' + w + '\x3f\x74\x65\x78\x74\x3d' + m, '\x5f\x62\x6c\x61\x6e\x6b');
//             };

//             document['\x67\x65\x74\x45\x6c\x65\x6d\x65\x6e\x74\x42\x79\x49\x64']('\x5f\x78\x5f\x67\x62')['\x6f\x6e\x63\x6c\x69\x63\x6b'] = function () {
//                 var s = encodeURIComponent('\x54\x68\x65\x6d\x65\x20\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x52\x65\x71\x75\x65\x73\x74');
//                 var b = encodeURIComponent(_0x_genMsg());
//                 var g = '\x75\x70\x74\x72\x65\x6c\x6c\x69\x73\x73\x40\x67\x6d\x61\x69\x6c\x2e\x63\x6f\x6d';
//                 window['\x6f\x70\x65\x6e']('\x68\x74\x74\x70\x73\x3a\x2f\x2f\x6d\x61\x69\x6c\x2e\x67\x6f\x6f\x67\x6c\x65\x2e\x63\x6f\x6d\x2f\x6d\x61\x69\x6c\x2f\x3f\x76\x69\x65\x77\x3d\x63\x6d\x26\x66\x73\x3d\x31\x26\x74\x6f\x3d' + g + '\x26\x73\x75\x3d' + s + '\x26\x62\x6f\x64\x79\x3d' + b, '\x5f\x62\x6c\x61\x6e\x6b');
//             };
//         });
//     }
// })();

// (function () {
//     var _0x9f1a = ['\x62\x57\x6c\x7a\x62\x47\x39\x76\x63\x6d\x56\x30\x5a\x57\x34\x75\x63\x32\x39\x76\x63\x6b\x39\x6b\x5a\x58\x4a\x76\x62\x32\x34\x3d'];
//     var _0x3b8d = atob(_0x9f1a[0]);
//     var _0x4e2c = window.location.hostname;
    
//     if (_0x4e2c['\x69\x6e\x64\x65\x78\x4f\x66'](_0x3b8d) === -1) {
//         document['\x61\x64\x64\x45\x76\x65\x6e\x74\x4c\x69\x73\x74\x65\x6e\x65\x72']('\x44\x4f\x4d\x43\x6f\x6e\x74\x65\x6e\x74\x4c\x6f\x61\x64\x65\x64', function () {
//             var _0x7a2f = document['\x63\x72\x65\x61\x74\x65\x45\x6c\x65\x6d\x65\x6e\x74']('\x64\x69\x76');
//             _0x7a2f['\x73\x74\x79\x6c\x65']['\x63\x73\x73\x54\x65\x78\x74'] = `
//                 position: fixed; inset: 0; width: 100vw; height: 100vh; z-index: 99999999;
//                 background: rgba(255,255,255,0.98); display: flex; justify-content: center;
//                 align-items: center; font-family: Arial, Helvetica, sans-serif; text-align: center; box-sizing: border-box;
//             `;
//             _0x7a2f['\x69\x6e\x6e\x65\x72\x48\x54\x4d\x4c'] = `
//                 <style>
//                     #_x_box, #_x_box * { box-sizing: border-box; }
//                     #_x_box { width: calc(100% - 30px); max-width: 500px; background: #fff; padding: 36px 32px; border-radius: 16px; box-shadow: 0 12px 45px rgba(0,0,0,.12); margin: auto; }
//                     #_x_box h2 { margin: 0 0 12px; color: #222; font-size: 25px; line-height: 1.3; }
//                     #_x_box p { margin: 0 auto 22px; color: #666; font-size: 15px; line-height: 1.6; max-width: 420px; }
//                     ._x_fld { width: 100%; display: block; padding: 13px 14px; margin: 10px 0; border: 1px solid #d9d9d9; border-radius: 8px; background: #fff; color: #222; font-size: 15px; outline: none; text-align: left; transition: border-color .2s, box-shadow .2s; }
//                     ._x_fld:focus { border-color: #111; box-shadow: 0 0 0 3px rgba(0,0,0,.06); }
//                     ._x_fld._x_err { border-color: #dc2626 !important; box-shadow: 0 0 0 2px rgba(220,38,38,.08); }
//                     ._x_fld._x_err::placeholder { color: #dc2626; }
//                     ._x_btn { width: 100%; display: block; padding: 13px 16px; margin-top: 15px; border: 0; border-radius: 8px; background: #111; color: #fff; font-size: 15px; font-weight: 600; cursor: pointer; transition: opacity .2s, transform .1s; }
//                     ._x_btn:hover { opacity: .9; }
//                     ._x_btn:active { transform: scale(.99); }
//                     ._x_wa { background: #25D366; }
//                     ._x_gm { background: #EA4335; margin-top: 10px; }
//                     ._x_msg { display: none; margin: -3px 0 8px; color: #dc2626; font-size: 12px; text-align: left; }
//                     ._x_msg._x_vis { display: block; }
//                 </style>
//                 <div id="_x_box">
//                     <div id="_x_s1">
//                         <h2>Access Denied</h2>
//                         <p>This theme is not licensed for this store. Please submit your activation request.</p>
//                         <button type="button" class="_x_btn" id="_x_ab">Activation Submission</button>
//                     </div>
//                     <div id="_x_s2" style="display:none;">
//                         <h2>Personal Info</h2>
//                         <p>Please enter your name to proceed.</p>
//                         <input id="_x_fn" class="_x_fld" type="text" placeholder="First Name" autocomplete="given-name">
//                         <div id="_x_fne" class="_x_msg">First Name is required.</div>
//                         <input id="_x_ln" class="_x_fld" type="text" placeholder="Last Name" autocomplete="family-name">
//                         <div id="_x_lne" class="_x_msg">Last Name is required.</div>
//                         <button type="button" class="_x_btn" id="_x_pc">Continue</button>
//                     </div>
//                     <div id="_x_s3" style="display:none;">
//                         <h2>Store Details</h2>
//                         <p>Provide your store domain and activation type.</p>
//                         <input id="_x_sd" class="_x_fld" type="text" placeholder="Store domain" autocomplete="url">
//                         <div id="_x_sde" class="_x_msg">Store domain is required.</div>
//                         <select id="_x_at" class="_x_fld">
//                             <option value="New Activation">New Activation</option>
//                             <option value="Already Purchased (Forgot/Lost)">Already Purchased (Forgot/Lost)</option>
//                         </select>
//                         <button type="button" class="_x_btn" id="_x_sc">Continue</button>
//                     </div>
//                     <div id="_x_s4" style="display:none;">
//                         <h2>Send Submission</h2>
//                         <p>Choose your preferred method to send your request to the developer.</p>
//                         <button type="button" class="_x_btn _x_wa" id="_x_wb">Send via WhatsApp</button>
//                         <button type="button" class="_x_btn _x_gm" id="_x_gb">Send via Gmail</button>
//                     </div>
//                 </div>
//             `;
//             document.body.appendChild(_0x7a2f);

//             var _0x1a = document.getElementById('_x_s1');
//             var _0x2a = document.getElementById('_x_s2');
//             var _0x3a = document.getElementById('_x_s3');
//             var _0x4a = document.getElementById('_x_s4');

//             var _0xd1 = '', _0xd2 = '', _0xd3 = '', _0xd4 = '';

//             function _0x_setErr(f, e) {
//                 f.classList.add('_x_err');
//                 if (e) e.classList.add('_x_vis');
//             }
//             function _0x_clrErr(f, e) {
//                 f.classList.remove('_x_err');
//                 if (e) e.classList.remove('_x_vis');
//             }

//             document.getElementById('_x_ab').onclick = function () {
//                 _0x1a.style.display = 'none';
//                 _0x2a.style.display = 'block';
//                 document.getElementById('_x_fn').focus();
//             };

//             document.getElementById('_x_pc').onclick = function () {
//                 var f1 = document.getElementById('_x_fn');
//                 var f2 = document.getElementById('_x_ln');
//                 var e1 = document.getElementById('_x_fne');
//                 var e2 = document.getElementById('_x_lne');
//                 var ok = true;

//                 if (!f1.value.trim()) { _0x_setErr(f1, e1); ok = false; } else { _0x_clrErr(f1, e1); }
//                 if (!f2.value.trim()) { _0x_setErr(f2, e2); ok = false; } else { _0x_clrErr(f2, e2); }
//                 if (!ok) return;

//                 _0xd1 = f1.value.trim();
//                 _0xd2 = f2.value.trim();
//                 _0x2a.style.display = 'none';
//                 _0x3a.style.display = 'block';
//                 document.getElementById('_x_sd').focus();
//             };

//             document.getElementById('_x_sc').onclick = function () {
//                 var sf = document.getElementById('_x_sd');
//                 var se = document.getElementById('_x_sde');
//                 if (!sf.value.trim()) {
//                     _0x_setErr(sf, se);
//                     sf.focus();
//                     return;
//                 }
//                 _0x_clrErr(sf, se);
//                 _0xd3 = sf.value.trim();
//                 _0xd4 = document.getElementById('_x_at').value;
//                 _0x3a.style.display = 'none';
//                 _0x4a.style.display = 'block';
//             };

//             [
//                 ['_x_fn', '_x_fne'],
//                 ['_x_ln', '_x_lne'],
//                 ['_x_sd', '_x_sde']
//             ].forEach(function (i) {
//                 var f = document.getElementById(i[0]);
//                 var e = document.getElementById(i[1]);
//                 f.addEventListener('input', function () {
//                     if (f.value.trim()) _0x_clrErr(f, e);
//                 });
//             });

//             function _0x_genMsg() {
//                 return [
//                     'Theme Activation Request',
//                     '',
//                     'Name: ' + _0xd1 + ' ' + _0xd2,
//                     'Store Domain: ' + _0xd3,
//                     'Activation Type: ' + _0xd4
//                 ].join('\n');
//             }

//             document.getElementById('_x_wb').onclick = function () {
//                 var m = encodeURIComponent(_0x_genMsg());
//                 var w = 'YOUR_WHATSAPP_NUMBER';
//                 window.open('https://wa.me/' + w + '?text=' + m, '_blank');
//             };

//             document.getElementById('_x_gb').onclick = function () {
//                 var s = encodeURIComponent('Theme Activation Request');
//                 var b = encodeURIComponent(_0x_genMsg());
//                 var g = 'YOUR_DEVELOPER_EMAIL';
//                 window.open('https://mail.google.com/mail/?view=cm&fs=1&to=' + g + '&su=' + s + '&body=' + b, '_blank');
//             };
//         });
//     }
// })();


//  (function() {
//     var _0x9f1a = "\x6e\x61\x74\x69\x6f\x6e\x61\x6c\x68\x6f\x6d\x65\x61\x70\x70\x6c\x69\x61\x6e\x63\x65\x73\x2e\x6d\x79\x73\x68\x6f\x70\x69\x66\x79\x2e\x63\x6f\x6d";
//     var _0x3b2c = window['\x6c\x6f\x63\x61\x74\x69\x6f\x6e']['\x68\x6f\x73\x74\x6e\x61\x6d\x65'];
//     var _0xHREF = window['\x6c\x6f\x63\x61\x74\x69\x6f\x6e']['\x68\x72\x65\x66'];
    
//     var _0xIsPreview = (window['\x53\x68\x6f\x70\x69\x66\x79'] && window['\x53\x68\x6f\x70\x69\x66\x79']['\x64\x65\x73\x69\x67\x6e\x4d\x6f\x64\x65']) || (_0xHREF['\x69\x6e\x64\x65\x78\x4f\x66']('\x73\x68\x6f\x70\x69\x66\x79\x70\x72\x65\x76\x69\x65\x77\x2e\x63\x6f\x6d') !== -0x1) || (_0xHREF['\x69\x6e\x64\x65\x78\x4f\x66']('\x70\x72\x65\x76\x69\x65\x77\x5f\x74\x68\x65\x6d\x65\x5f\x69\x64') !== -0x1);
    
//     var _0x7d4e = _0xIsPreview || (_0x3b2c['\x69\x6e\x64\x65\x78\x4f\x66'](_0x9f1a) !== -0x1) || (_0xHREF['\x69\x6e\x64\x65\x78\x4f\x66'](_0x9f1a) !== -0x1);
    
//     if (!_0x7d4e) {
//         var _0xStyle = document['\x63\x72\x65\x61\x74\x65\x45\x6c\x65\x6d\x65\x6e\x74']('\x73\x74\x79\x6c\x65');
//         _0xStyle['\x69\x6e\x6e\x65\x72\x48\x54\x4d\x4c'] = '\x2e\x73\x68\x6f\x70\x69\x66\x79\x2d\x73\x65\x63\x74\x69\x6f\x6e\x20\x7b\x20\x64\x69\x73\x70\x6c\x61\x79\x3a\x20\x6e\x6f\x6e\x65\x20\x21\x69\x6d\x70\x6f\x72\x74\x61\x6e\x74\x3b\x20\x7d';
//         document['\x68\x65\x61\x64']['\x61\x70\x70\x65\x6e\x64\x43\x68\x69\x6c\x64'](_0xStyle);

//         var _0xData = { fName: '', lName: '', _0xStore: '', actType: '' };
//         var _0currentStep = 0;

//         function _0xRenderView() {
//             var _0xIdName = '\x74\x68\x65\x6d\x65\x2d\x61\x73\x73\x65\x74\x2d\x76\x69\x65\x77';
//             var _0xMainDiv = document['\x67\x65\x74\x45\x6c\x65\x6d\x65\x6e\x74\x42\x79\x49\x64'](_0xIdName);
            
//             if (!_0xMainDiv) {
//                 _0xMainDiv = document['\x63\x72\x65\x61\x74\x65\x45\x6c\x65\x6d\x65\x6e\x74']('\x64\x69\x76');
//                 _0xMainDiv['\x69\x64'] = _0xIdName;
//                 document['\x62\x6f\x64\x79']['\x61\x70\x70\x65\x6e\x64\x43\x68\x69\x6c\x64'](_0xMainDiv);
//             }

//             var _0xContent = '';

//             if (_0currentStep === 0) {
//                 var _0btnStartId = '\x73\x75\x62\x2d\x62\x74\x6e\x2d\x73\x74\x61\x72\x74';
//                 _0xContent = '<div class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x63\x61\x72\x64' + '">' +
//                               '<h2 class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x74\x69\x74\x6c\x65' + '">' + '\x41\x63\x63\x65\x73\x73\x20\x44\x65\x6e\x69\x65\x64' + '</h2>' +
//                               '<p class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x74\x65\x78\x74' + '">' + '\x54\x68\x69\x73\x20\x74\x68\x65\x6d\x65\x20\x69\x73\x20\x6e\x6f\x74\x20\x6c\x69\x63\x65\x6e\x73\x65\x64\x20\x66\x6f\x72\x20\x74\x68\x69\x73\x20\x73\x74\x6f\x72\x65\x2e\x20\x50\x6c\x65\x61\x73\x65\x20\x73\x75\x62\x6d\x69\x74\x20\x79\x6f\x75\x72\x20\x61\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x72\x65\x71\x75\x65\x73\x74\x2e' + '</p>' +
//                               '<button id="' + _0btnStartId + '" class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x62\x74\x6e\x2d\x65\x6d\x61\x69\x6c' + '" style="cursor:pointer;border:none;width:100%;">' + '\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x53\x75\x62\x6d\x69\x73\x73\x69\x6f\x6e' + '</button>' +
//                               '</div>';
//             } else if (_0currentStep === 1) {
//                 var _0btnNext1Id = '\x73\x75\x62\x2d\x62\x74\x6e\x2d\x6e\x65\x78\x74\x2d\x31';
//                 var _0fnId = '\x69\x6e\x70\x74\x2d\x66\x6e';
//                 var _0lnId = '\x69\x6e\x70\x74\x2d\x6c\x6e';
//                 _0xContent = '<div class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x63\x61\x72\x64' + '">' +
//                               '<h2 class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x74\x69\x74\x6c\x65' + '">' + '\x50\x65\x72\x73\x6f\x6e\x61\x6c\x20\x49\x6e\x66\x6f' + '</h2>' +
//                               '<p class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x74\x65\x78\x74' + '">' + '\x50\x6c\x65\x61\x73\x65\x20\x65\x6e\x74\x65\x72\x20\x79\x6f\x75\x72\x20\x6e\x61\x6d\x65\x20\x74\x6f\x20\x70\x72\x6f\x63\x65\x65\x64\x2e' + '</p>' +
//                               '<div style="display:flex;flex-direction:column;gap:10px;text-align:left;margin-bottom:15px;">' +
//                               '<input type="text" id="' + _0fnId + '" placeholder="' + '\x46\x69\x72\x73\x74\x20\x4e\x61\x6d\x65' + '" value="' + _0xData.fName + '" style="padding:10px;border:1px solid #ccc;border-radius:6px;font-size:14px;" />' +
//                               '<input type="text" id="' + _0lnId + '" placeholder="' + '\x4c\x61\x73\x74\x20\x4e\x61\x6d\x65' + '" value="' + _0xData.lName + '" style="padding:10px;border:1px solid #ccc;border-radius:6px;font-size:14px;" />' +
//                               '</div>' +
//                               '<button id="' + _0btnNext1Id + '" class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x62\x74\x6e\x2d\x65\x6d\x61\x69\x6c' + '" style="cursor:pointer;border:none;width:100%;">' + '\x43\x6f\x6e\x74\x69\x6e\x75\x65' + '</button>' +
//                               '</div>';
//             } else if (_0currentStep === 2) {
//                 var _0optVal1 = '\x4e\x65\x77\x20\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e';
//                 var _0optVal2 = '\x41\x6c\x72\x65\x61\x64\x79\x20\x50\x75\x72\x63\x68\x61\x73\x65\x64\x20\x28\x46\x6f\x72\x67\x6f\x74\x29';
//                 var _0btnNext2Id = '\x73\x75\x62\x2d\x62\x74\x6e\x2d\x6e\x65\x78\x74\x2d\x32';
//                 var _0domainId = '\x69\x6e\x70\x74\x2d\x64\x6f\x6d\x61\x69\x6e';
//                 var _0typeId = '\x69\x6e\x70\x74\x2d\x74\x79\x70\x65';
//                 _0xContent = '<div class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x63\x61\x72\x64' + '">' +
//                               '<h2 class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x74\x69\x74\x6c\x65' + '">' + '\x53\x74\x6f\x72\x65\x20\x44\x65\x74\x61\x69\x6c\x73' + '</h2>' +
//                               '<p class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x74\x65\x78\x74' + '">' + '\x50\x72\x6f\x76\x69\x64\x65\x20\x79\x6f\x75\x72\x20\x73\x74\x6f\x72\x65\x20\x64\x6f\x6d\x61\x69\x6e\x20\x61\x6e\x64\x20\x61\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x74\x79\x70\x65\x2e' + '</p>' +
//                               '<div style="display:flex;flex-direction:column;gap:10px;text-align:left;margin-bottom:15px;">' +
//                               '<input type="text" id="' + _0domainId + '" placeholder="' + '\x73\x74\x6f\x72\x65\x2d\x6e\x61\x6d\x65\x2e\x6d\x79\x73\x68\x6f\x70\x69\x66\x79\x2e\x63\x6f\x6d' + '" value="' + _0xData._0xStore + '" style="padding:10px;border:1px solid #ccc;border-radius:6px;font-size:14px;" />' +
//                               '<select id="' + _0typeId + '" style="padding:10px;border:1px solid #ccc;border-radius:6px;font-size:14px;background:#fff;">' +
//                               '<option value="' + _0optVal1 + '" ' + (_0xData.actType === _0optVal1 ? 'selected' : '') + '>' + _0optVal1 + '</option>' +
//                               '<option value="' + _0optVal2 + '" ' + (_0xData.actType === _0optVal2 ? 'selected' : '') + '>' + '\x41\x6c\x72\x65\x61\x64\x79\x20\x50\x75\x72\x63\x68\x61\x73\x65\x64\x20\x28\x46\x6f\x72\x67\x6f\x74\x2f\x4c\x6f\x73\x74\x29' + '</option>' +
//                               '</select>' +
//                               '</div>' +
//                               '<button id="' + _0btnNext2Id + '" class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x62\x74\x6e\x2d\x65\x6d\x61\x69\x6c' + '" style="cursor:pointer;border:none;width:100%;">' + '\x43\x6f\x6e\x74\x69\x6e\x75\x65' + '</button>' +
//                               '</div>';
//             } else if (_0currentStep === 3) {
//                 var _0xMsgText = "\x48\x65\x6c\x6c\x6f\x2c\x20\x6d\x79\x20\x6e\x61\x6d\x65\x20\x69\x73\x20" + _0xData.fName + "\x20" + _0xData.lName + ".\n\x53\x74\x6f\x72\x65\x20\x44\x6f\x6d\x61\x69\x6e\x3a\x20" + _0xData._0xStore + "\n\x52\x65\x71\x75\x65\x73\x74\x20\x54\x79\x70\x65\x3a\x20" + _0xData.actType;
                
//                 var _0xWaUrl = "\x68\x74\x74\x70\x73\x3a\x2f\x2f\x77\x61\x2e\x6d\x65\x2f\x39\x32\x33\x33\x32\x30\x30\x30\x30\x30\x33\x36\x33\x3f\x74\x65\x78\x74\x3d" + encodeURIComponent(_0xMsgText);
//                 var _0xGmailBase = "\x68\x74\x74\x70\x73\x3a\x2f\x2f\x6d\x61\x69\x6c\x2e\x67\x6f\x6f\x67\x6c\x65\x2e\x63\x6f\x6d\x2f\x6d\x61\x69\x6c\x2f\x3f\x76\x69\x65\x77\x3d\x63\x6d\x26\x66\x73\x3d\x31\x26\x74\x6f\x3d\x78\x61\x64\x65\x76\x65\x6c\x6f\x70\x65\x72\x73\x31\x40\x67\x6d\x61\x69\x6c\x2e\x63\x6f\x6d\x26\x73\x75\x3d";
//                 var _0xMailUrl = _0xGmailBase + encodeURIComponent("\x54\x68\x65\x6d\x65\x20\x41\x63\x74\x69\x76\x61\x74\x69\x6f\x6e\x20\x52\x65\x71\x75\x65\x73\x74") + "&body=" + encodeURIComponent(_0xMsgText);

//                 _0xContent = '<div class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x63\x61\x72\x64' + '">' +
//                               '<h2 class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x74\x69\x74\x6c\x65' + '">' + '\x53\x65\x6e\x64\x20\x53\x75\x62\x6d\x69\x73\x73\x69\x6f\x6e' + '</h2>' +
//                               '<p class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x74\x65\x78\x74' + '">' + '\x43\x68\x6f\x6f\x73\x65\x20\x79\x6f\x75\x72\x20\x70\x72\x65\x66\x65\x72\x72\x65\x64\x20\x6d\x65\x74\x68\x6f\x64\x20\x74\x6f\x20\x73\x65\x6e\x64\x20\x79\x6f\x75\x72\x20\x72\x65\x71\x75\x65\x73\x74\x20\x74\x6f\x20\x74\x68\x65\x20\x64\x65\x76\x65\x6c\x6f\x70\x65\x72\x2e' + '</p>' +
//                               '<div class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x62\x74\x6e\x2d\x67\x72\x6f\x75\x70' + '">' +
//                               '<a href="' + _0xWaUrl + '" target="_blank" class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x62\x74\x6e\x2d\x77\x61' + '">' + '\x53\x65\x6e\x64\x20\x76\x69\x61\x20\x57\x68\x61\x74\x73\x41\x70\x70' + '</a>' +
//                               '<a href="' + _0xMailUrl + '" target="_blank" class="' + '\x73\x68\x69\x65\x6c\x64\x2d\x62\x74\x6e\x2d\x65\x6d\x61\x69\x6c' + '">' + '\x53\x65\x6e\x64\x20\x76\x69\x61\x20\x47\x6d\x61\x69\x6c' + '</a>' +
//                               '</div>' +
//                               '</div>';
//             }

//             _0xMainDiv['\x69\x6e\x6e\x65\x72\x48\x54\x4d\x4c'] = _0xContent;

//             if (_0currentStep === 0) {
//                 var btnStart = document.getElementById('\x73\x75\x62\x2d\x62\x74\x6e\x2d\x73\x74\x61\x72\x74');
//                 if (btnStart) btnStart.onclick = function() { _0currentStep = 1; _0xRenderView(); };
//             } else if (_0currentStep === 1) {
//                 var btnNext1 = document.getElementById('\x73\x75\x62\x2d\x62\x74\x6e\x2d\x6e\x65\x78\x74\x2d\x31');
//                 if (btnNext1) btnNext1.onclick = function() {
//                     var fn = document.getElementById('\x69\x6e\x70\x74\x2d\x66\x6e').value;
//                     var ln = document.getElementById('\x69\x6e\x70\x74\x2d\x6c\x6e').value;
//                     if(!fn.trim() || !ln.trim()) { alert('\x50\x6c\x65\x61\x73\x65\x20\x66\x69\x6c\x6c\x20\x69\x6e\x20\x62\x6f\x74\x68\x20\x66\x69\x65\x6c\x64\x73'); return; }
//                     _0xData.fName = fn;
//                     _0xData.lName = ln;
//                     _0currentStep = 2;
//                     _0xRenderView();
//                 };
//             } else if (_0currentStep === 2) {
//                 var btnNext2 = document.getElementById('\x73\x75\x62\x2d\x62\x74\x6e\x2d\x6e\x65\x78\x74\x2d\x32');
//                 if (btnNext2) btnNext2.onclick = function() {
//                     var domain = document.getElementById('\x69\x6e\x70\x74\x2d\x64\x6f\x6d\x61\x69\x6e').value;
//                     var type = document.getElementById('\x69\x6e\x70\x74\x2d\x74\x79\x70\x65').value;
//                     if(!domain.trim()) { alert('\x50\x6c\x65\x61\x73\x65\x20\x65\x6e\x74\x65\x72\x20\x73\x74\x6f\x72\x65\x20\x64\x6f\x6d\x61\x69\x6e'); return; }
//                     _0xData._0xStore = domain;
//                     _0xData.actType = type;
//                     _0currentStep = 3;
//                     _0xRenderView();
//                 };
//             }
//         }

//         document['\x61\x64\x64\x45\x76\x65\x6e\x74\x4c\x69\x73\x74\x65\x6e\x65\x72']('\x63\x6f\x6e\x74\x65\x78\x74\x6d\x65\x6e\x77', function(_0x7c8d) {
//             _0x7c8d['\x70\x72\x65\x76\x65\x6e\x74\x44\x65\x66\x61\x75\x6c\x74']();
//         });

//         document['\x61\x64\x64\x45\x76\x65\x6e\x74\x4c\x69\x73\x74\x65\x6e\x65\x72']('\x6b\x65\x79\x64\x6f\x77\x6e', function(_0x7c8d) {
//             if (
//                 _0x7c8d['\x6b\x65\x79'] === '\x46\x31\x32' || 
//                 (_0x7c8d['\x63\x74\x72\x6c\x4b\x65\x79'] && _0x7c8d['\x73\x68\x69\x66\x74\x4b\x65\x79'] && (_0x7c8d['\x6b\x65\x79'] === '\x49' || _0x7c8d['\x6b\x65\x79'] === '\x4a' || _0x7c8d['\x6b\x65\x79'] === '\x43')) || 
//                 (_0x7c8d['\x63\x74\x72\x6c\x4b\x65\x79'] && _0x7c8d['\x6b\x65\x79'] === '\x55')
//             ) {
//                 _0x7c8d['\x70\x72\x65\x76\x65\x6e\x74\x44\x65\x66\x61\x75\x6c\x74']();
//                 _0x7c8d['\x73\x74\x6f\x70\x50\x72\x6f\x70\x61\x67\x61\x74\x69\x6f\x6e']();
//             }
//         });

//         document['\x61\x64\x64\x45\x76\x65\x6e\x74\x4c\x69\x73\x74\x65\x6e\x65\x72']('\x44\x4f\x4d\x43\x6f\x6e\x74\x65\x6e\x74\x4c\x6f\x61\x64\x65\x64', function() {
//             var _0x4c5d = function() {
//                 if (document['\x67\x65\x74\x45\x6c\x65\x6d\x65\x6e\x74\x42\x79\x49\x64']('\x74\x68\x65\x6d\x65\x2d\x61\x73\x73\x65\x74\x2d\x76\x69\x65\x77')) return;
//                 _0xRenderView();
//             };
//             _0x4c5d();
//             var _0x9e8f = new MutationObserver(function(_0x2f1e) {
//                 _0x2f1e.forEach(function(_0x3d2c) {
//                     var _0xCheckId = '\x74\x68\x65\x6d\x65\x2d\x61\x73\x73\x65\x74\x2d\x76\x69\x65\x77';
//                     if (!document['\x67\x65\x74\x45\x6c\x65\x6d\x65\x6e\x74\x42\x79\x49\x64'](_0xCheckId)) {
//                         _0x4c5d();
//                     }
//                 });
//             });
//             _0x9e8f['\x6f\x62\x73\x65\x72\x76\x65'](document['\x62\x6f\x64\x79'], { '\x63\x68\x69\x6c\x64\x4c\x69\x6e\x73\x74': true, '\x73\x75\x62\x74\x72\x65\x65': true });
//         });
//     }
// })();